import React from 'react';
import { Button, Tooltip, Input } from '@heroui/react';
import {
  FilterSelect,
  DateFilterSelect,
  ModelSearchSelect,
  FilterOption,
  ResponsiveTooltip,
} from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import { ResetFiltersIc, SearchInputIc, AccountIcon } from '../../../../components/icons';
import { getBillStatuses } from '../constants';
import { FiltersProps } from '../types';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  hasActiveFilters,
  availableModels,
  modelsLoading,
  onModelSearchChange,
  onSearchChange,
  onFilterChange,
  onResetFilters,
}) => {
  const { t } = useTranslations();

  const handleDateRangeChange = (value: string) => {
    onFilterChange('dateRange', value);
    // Очищаем кастомные даты для всех предустановленных периодов
    if (value !== 'custom') {
      onFilterChange('customStartDate', null);
      onFilterChange('customEndDate', null);
    }
  };

  const handleCustomDateChange = (startDate: string, endDate: string) => {
    onFilterChange('customStartDate', startDate);
    onFilterChange('customEndDate', endDate);
    onFilterChange('dateRange', 'custom');
  };

  return (
    <div className="px-4 pb-6 md:px-6 md:pb-6 flex items-center gap-4 flex-wrap justify-between w-full">
      <div className="flex items-center gap-4 md:flex-wrap md:justify-start max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        {/* Search input */}
        <Input
          type="text"
          placeholder={t('client.revshare.bills.filters.searchPlaceholder')}
          size="sm"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[12.5rem]',
            base: 'w-[12.5rem]',
            input: 'text-sm text-auth-ink placeholder:text-neutral-700',
            inputWrapper:
              'rounded-2 h-9 w-[12.5rem] bg-auth-surface border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
            innerWrapper: 'bg-transparent',
          }}
        />

        <DateFilterSelect
          placeholder={t('client.revshare.bills.filters.date')}
          selectedKey={filters.dateRange}
          onSelectionChange={handleDateRangeChange}
          onCustomDateChange={handleCustomDateChange}
          customStartDate={filters.customStartDate || undefined}
          customEndDate={filters.customEndDate || undefined}
          variant="campaign"
          startContent={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
              <path
                fill="none"
                stroke="#A8A8B0"
                strokeLinecap="round"
                strokeWidth="1.5"
                d="M8 2v3m8-3v3M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
              />
            </svg>
          }
        />

        <ModelSearchSelect
          placeholder={t('client.revshare.bills.filters.account')}
          options={[
            ...availableModels.map(
              (model) =>
                ({
                  key: model.id,
                  avatar: model.avatar,
                  username: model.username,
                  name: model.name,
                  label: `${model.name} (@${model.username})`,
                }) as FilterOption
            ),
          ]}
          selectedKey={filters.model_id || 'all'}
          onSelectionChange={(value) => {
            const selected = value === 'all' ? null : value;
            onFilterChange('model_id', selected);
          }}
          onSearchChange={onModelSearchChange}
          isLoading={modelsLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
          searchPlaceholder={t('client.revshare.bills.filters.accountSearchPlaceholder')}
        />

        <FilterSelect
          placeholder={t('client.revshare.bills.filters.status')}
          options={getBillStatuses(t)}
          selectedKey={filters.status}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('status', value)}
          startContent={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M4.72458 8.26132L7.27637 4.80879C8.92584 2.57708 9.75057 1.46122 10.52 1.69703C11.2895 1.93283 11.2895 3.30142 11.2895 6.03861V6.2967C11.2895 7.28394 11.2895 7.77757 11.605 8.08719L11.6216 8.10323C11.9439 8.40632 12.4577 8.40632 13.4852 8.40632C15.3342 8.40632 16.2588 8.40632 16.5712 8.9671C16.5764 8.97639 16.5814 8.98575 16.5863 8.99518C16.8813 9.56465 16.346 10.2889 15.2753 11.7374L12.7236 15.1899C11.0741 17.4216 10.2493 18.5375 9.47985 18.3017C8.71038 18.0659 8.7104 16.6972 8.71044 13.96L8.71044 13.7021C8.71046 12.7148 8.71046 12.2212 8.39501 11.9115L8.37832 11.8955C8.05607 11.5924 7.5423 11.5924 6.51477 11.5924C4.66571 11.5924 3.74119 11.5924 3.42873 11.0316C3.42355 11.0223 3.41852 11.013 3.41364 11.0036C3.11868 10.4341 3.65398 9.70983 4.72458 8.26132Z"
                stroke="#A8A8B0"
                strokeWidth="1.5"
              />
            </svg>
          }
          autoWidth={true}
          showPlaceholderWithValue={true}
        />

        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}

        {hasActiveFilters && (
          <Tooltip content={t('client.revshare.bills.filters.resetTooltip')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default FiltersSection;
